import { useTranslate } from '@tolgee/react';
import { PickerPopover } from '../PickerPopover';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { useCallback, useMemo, useState } from 'react';
import { Button, message, Spin, Tooltip, Empty } from 'antd';
import { SearchInput } from 'components/LightConversation/components/SearchInput';
import { useVoucherSearchOptions } from 'hooks/business/chat/useChatSide';
import { CouponItem } from './CouponItem';
import { useVoucherCanCreate } from 'hooks/business/useVoucherOperate';
import { PlusOutlined } from '@ant-design/icons';
import { CouponPickerProvider, useCouponPickerContext } from './CouponPickerContext';
import { ExpireCouponModal } from './ExpireCouponModal';
import { VoucherSync } from 'views/Conversation/Sider/VoucherSync';
import { ToolbarIcon } from '../../ToolbarIcon';

interface CouponPickerProps {
  conversation: Conversation.Conversation;
}

export const CouponPicker = (props: CouponPickerProps) => {
  const { conversation } = props;
  const { t } = useTranslate();
  const { storeId, channel } = conversation;
  const [visible, setVisible] = useState(false);
  const [keywordType, setKeywordType] = useState<Conversation.Voucher.SearchKeywordType>(0);
  const [keyword, setKeyword] = useState('');

  const { canCreate } = useVoucherCanCreate(conversation);
  const voucherSearchOptions = useVoucherSearchOptions(channel);

  const searchOptions = useMemo(
    () =>
      voucherSearchOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
      })),
    [voucherSearchOptions],
  );

  const ongoingParams = useMemo(
    () => ({
      internal_store_id: storeId,
      voucher_status: 'ONGOING' as Conversation.Voucher.Status,
      searchKeyword: keyword || undefined,
      keywordType: keyword ? keywordType : undefined,
    }),
    [storeId, keyword, keywordType],
  );

  const scheduledParams = useMemo(
    () => ({
      internal_store_id: storeId,
      voucher_status: 'SCHEDULED' as Conversation.Voucher.Status,
      searchKeyword: keyword || undefined,
      keywordType: keyword ? keywordType : undefined,
    }),
    [storeId, keyword, keywordType],
  );

  const {
    data: onGoingCoupons,
    isLoading: ongoingLoading,
    isError: ongoingError,
  } = useQuery({
    ...queries.coupons.list(ongoingParams),
    keepPreviousData: true,
    enabled: visible,
    onError: () => {
      message.error(t('chatWorkspace.couponPicker.toast.fetchFailed'));
    },
  });

  const lastSyncTime = onGoingCoupons?.data?.lastSyncTime;

  const {
    data: scheduledCoupons,
    isLoading: scheduledLoading,
    isError: scheduledError,
  } = useQuery({
    ...queries.coupons.list(scheduledParams),
    keepPreviousData: true,
    enabled: visible,
    onError: () => {
      message.error(t('chatWorkspace.couponPicker.toast.fetchFailed'));
    },
  });

  const ongoingList = useMemo(() => onGoingCoupons?.data?.data || [], [onGoingCoupons]) as
    | Conversation.Voucher.Vo[]
    | undefined;

  const scheduledList = useMemo(() => scheduledCoupons?.data?.data || [], [scheduledCoupons]) as
    | Conversation.Voucher.Vo[]
    | undefined;

  const list = useMemo(
    () => [...(ongoingList || []), ...(scheduledList || [])],
    [ongoingList, scheduledList],
  );
  const loading = ongoingLoading || scheduledLoading;
  const error = ongoingError || scheduledError;
  const empty = !loading && list.length === 0;

  const handleSelectChange = useCallback((value: string) => {
    setKeywordType(Number(value) as Conversation.Voucher.SearchKeywordType);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setKeyword(value);
  }, []);

  const queryClient = useQueryClient();
  const handleVoucherDelete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queries.coupons.list(ongoingParams).queryKey });
    queryClient.invalidateQueries({ queryKey: queries.coupons.list(scheduledParams).queryKey });
  }, [queryClient, ongoingParams, scheduledParams]);
  const handleVoucherUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queries.coupons.list(ongoingParams).queryKey });
    queryClient.invalidateQueries({ queryKey: queries.coupons.list(scheduledParams).queryKey });
  }, [queryClient, ongoingParams, scheduledParams]);

  return (
    <PickerPopover
      title={t('chatWorkspace.couponPicker.title')}
      icon={
        <Tooltip title={t('chatWorkspace.couponPicker.title')}>
          <ToolbarIcon icon="coupon" />
        </Tooltip>
      }
      headerExtra={
        <VoucherSync
          storeId={storeId}
          channel={channel}
          lastSyncTime={lastSyncTime}
          onSuccess={handleVoucherUpdate}
        />
      }
      onVisibleChange={(next) => {
        setVisible(next);
      }}
    >
      <CouponPickerProvider conversation={conversation} afterSubmit={handleVoucherUpdate}>
        <CouponPickerContent
          conversation={conversation}
          canCreate={canCreate}
          searchOptions={searchOptions}
          keywordType={keywordType}
          onSelectChange={handleSelectChange}
          onSearch={handleSearch}
          keyword={keyword}
          loading={loading}
          error={error}
          empty={empty}
          list={list}
          onVoucherDelete={handleVoucherDelete}
          onVoucherUpdate={handleVoucherUpdate}
        />
      </CouponPickerProvider>
    </PickerPopover>
  );
};

interface CouponPickerContentProps {
  conversation: Conversation.Conversation;
  canCreate: boolean;
  searchOptions: { value: string; label: string }[];
  keywordType: Conversation.Voucher.SearchKeywordType;
  onSelectChange: (value: string) => void;
  onSearch: (value: string) => void;
  keyword: string;
  loading: boolean;
  error: boolean;
  empty: boolean;
  list: Conversation.Voucher.Vo[];
  onVoucherDelete: () => void;
  onVoucherUpdate: () => void;
}

function CouponPickerContent(props: CouponPickerContentProps) {
  const { t } = useTranslate();
  const { openCreate, openEdit, openCopy } = useCouponPickerContext()!;
  const {
    conversation,
    canCreate,
    searchOptions,
    keywordType,
    onSelectChange,
    onSearch,
    loading,
    error,
    empty,
    list,
    onVoucherDelete,
    onVoucherUpdate,
  } = props;

  let content = null;

  if (loading) {
    content = (
      <div className="flex justify-center py-6">
        <Spin />
      </div>
    );
  } else if (error) {
    content = (
      <Empty
        className="pt-4"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('chatWorkspace.couponPicker.toast.fetchFailed')}
      />
    );
  } else if (empty) {
    content = (
      <Empty
        className="pt-4"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('chatWorkspace.couponPicker.empty')}
      />
    );
  } else {
    content = (
      <div className="grid grid-cols-2 gap-3">
        {list.map((voucher) => (
          <CouponItem
            key={voucher.internalVoucherId}
            coupon={voucher}
            conversation={conversation}
            onDelete={onVoucherDelete}
            onUpdate={onVoucherUpdate}
            onEdit={openEdit}
            onCopyAndCreate={openCopy}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col w-[1000px] min-h-[280px] max-h-[60vh]">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="max-w-[360px]">
          <SearchInput
            placeholder={t('common.placeholder.search')}
            selectOptions={searchOptions}
            selectValue={String(keywordType)}
            onSelectChange={onSelectChange}
            onSearch={onSearch}
          />
        </div>
        <div className="flex items-center gap-2">
          <ExpireCouponModal conversation={conversation} />
          {canCreate && (
            <Button icon={<PlusOutlined />} type="primary" onClick={openCreate}>
              {t('chatWorkspace.couponPicker.button.create')}
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-4">{content}</div>
    </div>
  );
}
